import { roundDecimalPlaces, distancePt, randomFromRange } from './utils.js'

// This class deals with filling/expanding shapes 
// based on their parameters and some arguments. 
// Will probably be most aimed at circles but can see what else it might 
// work for
class ShapeFillInterleave {

    constructor(
        shape
    ) {
        // shape needs a getCoordinates() method for this to work.  
        this.shape = shape
    }


    getSubSizes(args) {
        let completeSize = args.coverage / args.curveCount
        if (args.subSizes.length === 0) {
            return Array(args.curveCount).fill(completeSize)
        } else {
            let normalize = args.subSizes.reduce((a, b) => a + b)
            let subs = args.subSizes.map(a => (a / normalize) * completeSize)
            return Array(args.curveCount).fill(subs).flat()
        }

    }


    getSwirlOffset(args, i) {
        return args.rotation * i + args.startParam
    }


    getPadLength(args, curveCoverage) {
        let padLengths = []
        let blankSize = 1 - curveCoverage
        if (args.subSizes.length > 1) {
            for (let i = 0; i < args.curveCount; i++) {
                for (let sub of args.subSizes) {
                    padLengths.push((blankSize / args.curveCount) * sub)
                }
            }
        } else {
            padLengths = Array(args.curveCount).fill(blankSize / args.curveCount)
        }
        return padLengths
    }


    addInterleavedParams() {

    }


    // Based on the inputs, calculate the center points (1 dim arr)
    // for all of the curves, and then all the curve points moving outward 
    // in a 2-dim array, with pairs in that array representing the 
    // curves endpoints in an outward progression.  
    getDistanceParams(args) {
        let centerParams = []
        let endParams = []
        let padLengths = this.getPadLength(args, args.coverage)
        let allCurveSizes = this.getSubSizes(args)
        let originalCurveSizes = allCurveSizes.slice(0)
        for (let i = 0; i < args.maxLayers; i++) {
            let startOffset = this.getSwirlOffset(args, i)
            centerParams.push([])
            endParams.push([])
            let prevParam = startOffset
            let n = 0
            for (let size of allCurveSizes) {
                // Setup the equation.
                // The form is:
                // outer1 + outer2 == size
                // inner1 + inner == size
                // So anything you do to either inner/outer 
                // you must make sure to exactly offset in the other.
                let equalSizes = size / 2
                let inOutReall = equalSizes * (1 - args.innerPct)
                let outer1 = equalSizes + inOutReall
                let outer2 = equalSizes - inOutReall
                let inner1 = equalSizes - inOutReall
                let inner2 = equalSizes + inOutReall
                let altReall1 = outer2 * (args.alternateDiff)
                let altReall2 = inner2 * (args.alternateDiff)
                outer1 = outer1 + altReall1
                outer2 = outer2 - altReall1
                inner1 = inner1 + altReall2
                inner2 = inner2 - altReall2
                let innerOffset = (outer1 - inner1) / 2
                let halfPad = padLengths[n] / 2

                // The points for the first half of the shape
                // let start = halfPad + prevParam
                let start = prevParam + halfPad
                let center = (outer1 / 2) + start
                let outer1End = start + outer1
                centerParams.at(-1).push(roundDecimalPlaces(center, 5))
                endParams.at(-1).push(roundDecimalPlaces(start, 5))
                endParams.at(-1).push(roundDecimalPlaces(outer1End, 5))

                // points for the second half; could be inside
                // or outside the above
                // let secondSize = partSize * args.innerPct
                let innerStart = start + innerOffset
                let inner1End = innerStart + inner1
                endParams.at(-1).push(roundDecimalPlaces(innerStart, 5))
                endParams.at(-1).push(roundDecimalPlaces(inner1End, 5))

          
                // Do the interleaving
                let interL = []
                // The outer part of the second shape
                let outer2Start = outer1End + halfPad
                let outer2End = outer2Start + outer2
                interL.push(outer2Start)
                interL.push(outer2End)
                // the outer part of the second shape
                let inner2Start = inner1End + halfPad
                interL.push(inner2Start)
                interL.push(inner2Start + inner2)
                endParams.at(-1).push(...interL)

                // Reset the start location
                prevParam = outer2End
                n++
            }
            if (args.changeCompound) {
                allCurveSizes = allCurveSizes.map(size => size * (1 + args.change))
            } else {
                allCurveSizes = originalCurveSizes.map(size => size * (1 + args.change * (i + 1)))
            }
            // Alter the pad lengths to account for size change
            let curveTotal = allCurveSizes.reduce((a, b) => a + b)
            padLengths = this.getPadLength(args, curveTotal)
        }
        return [centerParams, endParams]
    }


    // The curve points are a 3 dim array to account for being 
    // different for each input shape, while the params will be the same 
    // for each of them
    getCurvePoints(endsParams, args) {
        let curvePoints = []
        let thicknesses = []
        let spacings = []
        let distance = 1
        let n = 0
        for (let params of endsParams) {
            curvePoints.push([])
            let thickLen = args.thickness.length
            let spaceLen = args.spacing.length
            let thickness = thickLen > 2 ? args.thickness[n % thickLen] : roundDecimalPlaces(randomFromRange(...args.thickness), 3)
            let spacing = spaceLen > 2 ? args.spacing[n % spaceLen] : roundDecimalPlaces(randomFromRange(...args.spacing), 3)
            thicknesses.push(thickness)
            spacings.push(spacing)
            for (let i = 0; i < params.length - 1; i+=4) {
                curvePoints.at(-1).push(distancePt(this.shape, args.centerPt, params.at(i), distance))
                curvePoints.at(-1).push(distancePt(this.shape, args.centerPt, params.at(i + 1), distance))
                curvePoints.at(-1).push(distancePt(this.shape, args.centerPt, params.at(i + 3), distance + thickness))
                curvePoints.at(-1).push(distancePt(this.shape, args.centerPt, params.at(i + 2), distance + thickness))
            }
            distance += thickness + spacing
            n++
        }
        return [curvePoints, thicknesses, spacings]
    }


    buildCurve(pointArr, paramsArc1, paramsArc2, sizeArr, connectPoints) {
        let path = ``
        let [startInnerPt, endInnerPt, startOuterPt, endOuterPt] = pointArr
        let [sizeInner, sizeOuter] = sizeArr

        // Go to start
        path = `${path} M ${startInnerPt.x} ${startInnerPt.y} `

        // draw to inner arc end
        path = connectPoints ? `${path} L ${endInnerPt.x} ${endInnerPt.y} ` : `${path} ${this.shape.getSubCurveRelative(...paramsArc1, sizeInner)} `

        // line to outer arc start
        path = `${path} L ${startOuterPt.x} ${startOuterPt.y} `

        // draw outer arc
        path = connectPoints ? `${path} L ${endOuterPt.x} ${endOuterPt.y} ` : `${path} ${this.shape.getSubCurveRelative(...paramsArc2.reverse(), sizeOuter)} `

        // line back to start point
        path = `${path} L ${startInnerPt.x} ${startInnerPt.y} `
        return path
    }


    getLayerShapes(curvePoints, thicknesses, spacings, endParams, args) {
        let shapePaths = []
        let n = 1
        for (let layerPoints of curvePoints) {
            let thicknessSum = thicknesses.slice(0, n - 1).reduce((a, b) => a + b, 0)
            let thicknessSumCur = thicknesses.slice(0, n).reduce((a, b) => a + b, 0)
            let spacingSum = spacings.slice(0, n - 1).reduce((a, b) => a + b, 0)
            // let spacingSum = spacings.slice(0, n).reduce((a, b) => a + b)
            let layerParams = endParams.at(n - 1)

            let sizeInner = 1 + (thicknessSum + spacingSum)
            let sizeOuter = 1 + (thicknessSumCur + spacingSum)

            // let sizeInner = 1 + ((n - 1) * (args.thickness + args.spacing))
            // let sizeOuter = 1 + (n * args.thickness) + ((n - 1) * args.spacing)
            for (let i = 0; i < layerPoints.length - 3; i += 4) {
                let paramsArc1 = layerParams.slice(i, i + 2)
                let paramsArc2 = layerParams.slice(i + 2, i + 4)
                let points = layerPoints.slice(i, i + 4)
                shapePaths.push(this.buildCurve(points, paramsArc1, paramsArc2, [sizeInner, sizeOuter], false))
            }
            n++
        }
        return shapePaths
    }

    fillShape(args) {
        let [centerParams, endParams] = this.getDistanceParams(args)
        let [curvePoints, thicknesses, spacings] = this.getCurvePoints(endParams, args)
        let getLayerShapes = this.getLayerShapes(curvePoints, thicknesses, spacings, endParams, args)
        return [getLayerShapes, curvePoints]
    }

}

export { ShapeFillInterleave }
